import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Existing elements
import AvailableCitiesModal from '../elements/available-cities-modal';

// Premium components
import {
  SectionWrapper,
  Breadcrumbs,
  BlogCard,
  FinalCTA,
  StructuredData,
  FaqAccordion,
} from '../elements/premium';
import { startingPriceLabel } from '../../utils/pricing';

// In-memory cache for blog content
const blogCache = {};

// Re-point generic in-body /service links to the matched near-me booking page so
// blog readers funnel into the page that ranks/converts for that intent. Render-time
// only (never edits the generated blog JSON); falls back to /service when no match.
// car-oil rule is placed BEFORE the generic oil rule so car-oil blogs route to the
// car page, not /bike-oil-change.
const BLOG_DEEPLINK_RULES = [
  [/car\s*ac|a\/c|air\s*condition/i, '/car-ac-repair-near-me'],
  [/car\s*battery|battery\s*(replace|cost)/i, '/car-battery-replacement-near-me'],
  [/car.*oil/i, '/car-engine-oil-change-near-me'],
  [/car.*(service|maintenance)/i, '/car-service-near-me'],
  [/car.*(repair|mechanic)/i, '/car-repair-near-me'],
  [/(bike|scooter|two[-\s]?wheeler).*(service|maintenance)/i, '/bike-service-near-me'],
  [/(bike|scooter|two[-\s]?wheeler).*(repair|mechanic)/i, '/bike-repair-near-me'],
  [/puncture|tyre|tire/i, '/bike-puncture-repair-near-me'],
  [/(engine\s*)?oil|lubric/i, '/bike-oil-change'],
];
function deepLinkBlogBody(html, blogItem) {
  if (!html) return html;
  const hay = `${(blogItem && blogItem.slug) || ''} ${(blogItem && blogItem.title) || ''} ${(blogItem && blogItem.category) || ''}`;
  let target = null;
  for (const [re, href] of BLOG_DEEPLINK_RULES) { if (re.test(hay)) { target = href; break; } }
  if (!target) return html;
  return html
    .replace(/href=("|')\/service\1/g, `href=$1${target}$1`)
    .replace(/\]\(\/service\)/g, `](${target})`);
}

class BlogDetails extends Component {
  constructor(props) {
    super(props);
    // Check for SSR-injected blog data (avoids "Loading article..." flash on hydration)
    const slug = props.match && props.match.params ? props.match.params.slug : null;
    let preloaded = null;
    if (typeof window !== 'undefined' && window.__SSR_BLOG_DATA__ && slug) {
      preloaded = window.__SSR_BLOG_DATA__;
      blogCache[slug] = preloaded;
      blogCache[preloaded.slug] = preloaded;
      delete window.__SSR_BLOG_DATA__;
    }
    this.state = {
      blogItem: preloaded || null,
      loading: !preloaded,
      error: false,
    };
  }

  createSlug = (title) =>
    title
      .toLowerCase()
      .replace(/[\u2013\u2014]+/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  componentDidMount() {
    this.loadBlog();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.slug !== this.props.match.params.slug) {
      this.loadBlog();
    }
  }

  loadBlog = async () => {
    const { slug } = this.props.match.params;

    if (blogCache[slug]) {
      this.setState({ blogItem: blogCache[slug], loading: false, error: false });
      return;
    }

    this.setState({ loading: true, error: false });

    try {
      let response = await fetch(`/blogs/${slug}.json`);

      if (!response.ok) {
        const indexResponse = await fetch('/blogs-index.json');
        if (indexResponse.ok) {
          const index = await indexResponse.json();
          const matchedBlog = index.find((item) => {
            const generatedSlug = this.createSlug(item.title);
            return generatedSlug === slug || item.slug === slug;
          });
          if (matchedBlog && matchedBlog.slug) {
            response = await fetch(`/blogs/${matchedBlog.slug}.json`);
          }
        }
      }

      if (response.ok) {
        const blogData = await response.json();
        blogCache[slug] = blogData;
        blogCache[blogData.slug] = blogData;
        this.setState({ blogItem: blogData, loading: false, error: false });
      } else {
        this.setState({ blogItem: null, loading: false, error: true });
      }
    } catch (error) {
      this.setState({ blogItem: null, loading: false, error: true });
    }
  };

  getRelatedBlogs() {
    const { blogsData = [] } = this.props;
    const { blogItem } = this.state;
    if (!blogItem || !blogsData.length) return [];

    // Try to find blogs in the same category, exclude current
    const currentSlug = this.props.match.params.slug;
    const sameCategory = blogsData.filter(
      (b) => b.category === blogItem.category && b.slug !== currentSlug
    );
    if (sameCategory.length >= 3) return sameCategory.slice(0, 3);

    // Fallback: any blogs except current
    return blogsData.filter((b) => b.slug !== currentSlug).slice(0, 3);
  }

  render() {
    const { blogItem, loading, error } = this.state;
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    // Loading state
    if (loading) {
      return (
        <div className="rr-home rr-home--lux">
          <Header user={user} setUser={setUser} isMobileApp={isMobileApp} isMobileBrowser={isMobileBrowser} transparent={false} />
          <div style={{ padding: '120px 20px 80px', textAlign: 'center', minHeight: '60vh' }}>
            <p style={{ fontSize: 18, color: '#555' }}>Loading article...</p>
          </div>
          <Footer setCitiesModal={setCitiesModal} isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} />
        </div>
      );
    }

    // Not found
    if (error || !blogItem) {
      return <Redirect to="/error-404" />;
    }

    const slug = this.props.match.params.slug;
    const canonical = `https://www.ridenrepair.com/blog-details/${slug}`;
    const relatedBlogs = this.getRelatedBlogs();

    // Breadcrumbs
    const breadcrumbs = [
      { label: 'Home', href: '/' },
      { label: 'Blogs', href: '/blogs' },
      { label: blogItem.title },
    ];

    // Structured data
    const schema = {
      '@type': 'Article',
      headline: blogItem.title,
      description: blogItem.summary || '',
      url: canonical,
      author: { '@type': 'Organization', name: 'Ride N Repair' },
      publisher: {
        '@type': 'Organization',
        name: 'Ride N Repair',
        logo: { '@type': 'ImageObject', url: 'https://www.ridenrepair.com/images/logo.webp' },
      },
      ...(blogItem.date && { datePublished: blogItem.date }),
      ...(blogItem.dateModified ? { dateModified: blogItem.dateModified } : blogItem.date ? { dateModified: blogItem.date } : {}),
      ...(blogItem.thumb && { image: blogItem.thumb }),
    };

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{blogItem.title} — Ride N Repair Blog</title>
          <meta name="description" content={blogItem.summary || ''} />
          <link rel="canonical" href={canonical} />
          <link rel="alternate" hrefLang="en-in" href={canonical} />
          <link rel="alternate" hrefLang="x-default" href={canonical} />
          <meta property="og:title" content={blogItem.title} />
          <meta property="og:description" content={blogItem.summary || ''} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="article" />
          {blogItem.thumb && <meta property="og:image" content={blogItem.thumb} />}
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} transparent={false} />

        <Breadcrumbs items={breadcrumbs} />

        {/* ARTICLE */}
        <section className="rr-section">
          <div className="rr-container--narrow">
            {/* Article header */}
            <div className="rr-blog-detail__header">
              {blogItem.category && <span className="rr-blog-detail__category">{blogItem.category}</span>}
              <h1 className="rr-blog-detail__title">{blogItem.title}</h1>
              <div className="rr-blog-detail__meta">
                {blogItem.date && <span>{blogItem.date}</span>}
                {blogItem.readTime && <span>{blogItem.readTime}</span>}
                <span>By Ride N Repair</span>
              </div>
            </div>

            {/* Featured image */}
            {blogItem.thumb && (
              <div className="rr-blog-detail__hero-img">
                <img src={blogItem.thumb} alt={blogItem.title} loading="eager" />
              </div>
            )}

            {/* Article body */}
            <div
              className="rr-blog-detail__body"
              dangerouslySetInnerHTML={{ __html: deepLinkBlogBody(blogItem.blogText, blogItem) }}
            />

            {/* FAQ (visible) — schema is emitted server-side, so render withSchema={false} to avoid a duplicate */}
            {Array.isArray(blogItem.faqSchema) && blogItem.faqSchema.length > 0 && (
              <div className="rr-blog-detail__faq">
                <h2 className="rr-blog-detail__faq-title">Frequently Asked Questions</h2>
                <FaqAccordion
                  faqs={blogItem.faqSchema.map((f) => ({ q: f.question, a: f.answer }))}
                  withSchema={false}
                />
              </div>
            )}

            {/* Service CTA */}
            <div className="rr-blog-detail__service-cta">
              <p>Need vehicle service or repair?</p>
              <Link to="/service" className="rr-cta-primary">{`Book Doorstep Service — Starting ${startingPriceLabel('site')}`}</Link>
            </div>
          </div>
        </section>

        {/* RELATED BLOGS */}
        {relatedBlogs.length > 0 && (
          <SectionWrapper variant="gray" kicker="More Articles" heading="Related Blog Posts">
            <div className="rr-card-grid--3col">
              {relatedBlogs.map((blog, i) => (
                <BlogCard
                  key={blog.slug || i}
                  slug={blog.slug}
                  title={blog.title}
                  excerpt={blog.summary || blog.blogText}
                  image={blog.thumb}
                  category={blog.category}
                  date={blog.date}
                  readTime={blog.readTime}
                />
              ))}
            </div>
          </SectionWrapper>
        )}

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Need Vehicle Service?<br />{`Book Now — Starting ${startingPriceLabel('site')}.`}</>}
          subtitle="Doorstep service · Certified mechanics · 30-day warranty"
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default BlogDetails;
